import { MessageItem, NiceBadge } from "@/ui/CommunicationView/ChatConversation/PromptMessageContainer/PromptMessageContainer";
import { PageSpecFromStructuredMessage } from "../../Artifacts/components/page_builder/PageSpec"
import { SectionBuilderFromStructuredMessage } from "../../Artifacts/components/page_builder/SectionBuilder"
import { PageCopywritingFromStructuredMessage } from "../../Artifacts/components/page_builder/PageCopywriting";
import { CampaignSpecFromStructuredMessage } from "../../Artifacts/components/campaign_builder/CampaignSpec";
import { BuildingNodeContent, CampaignBuilderArtboardFromStructuredMessage, CampaignBuilderFromStructuredMessage } from "../../Artifacts/components/campaign_builder/CampaignBuilder";
import { CardsThree, HandSwipeRight, PaintBrush, PencilSimpleLine, Target } from "@phosphor-icons/react";
import { BrainIcon, Trash } from "lucide-react";
// import ReactJson from "react-json-view";
import { ArtifactType } from "@/ui/CommunicationView/ChatConversation/types";
import rehypeRaw from "rehype-raw";
import { Markdown } from "react-markdown/lib";
import remarkBreaks from "remark-breaks";
import { markdownComponents } from "@/ui/CommunicationView/ChatConversation/PromptMessageContainer/markdown";
import { Loading } from "@/ui/Layout/Loading";

const sectionArtifacts = [
  "Hero_Section",
  "Benefit_Section",
  "CTA_Section",
  "Content_Section",
  "Pricing_Section",
  "SocialProof_Section",
  "Form_Section",
  "Navigation_Section",
  "Footer_Section",
  "Guarantee_Section",
  "Urgency_Section",
].map((name) => ({
  id: name,
  title: `Added: ${name.replace("_", " ")}`,
  component: () => <NiceBadge caption={`Added: ${name.replace("_", " ")}`} />,
}));

const pageElementArtifacts: ArtifactType[] = [
  "headlines",
  "text",
  "buttons",
  "pictures",
  "videos",
  "dividers",
  "formFields",
  "progress",
  "spacers",
  "listItems",
  "countdowns",
  "navigations",
  "sections",
  "containers",
  "column",
  "columns",
].map((id) => ({
  id: id,
  type: "tool_call",
  // component: (props: any) => <ReactJson src={props} name={id} collapsed />,
  component: ({ payload }: { payload: any, isLoading: boolean }) => (
    <NiceBadge
      caption={`Building: ${payload?.type || "Page Element"}`}
      // tooltip={JSON.stringify(payload, null, 2)}
      onClick={() => {
        // scroll to the the component in the canvas
        document
          .getElementById(payload?.id)
          ?.scrollIntoView({ behavior: "smooth" });
      }}
    />
  ),
}));

const pageBuildingToolArtifacts: ArtifactType[] = [
  ...[
    "create_page_elements",
    "create_group_of_elements",
    "create_one_page_section",
    "create_one_complete_page",
    "insert_item",
  ].map((id) => ({
    id,
    title: "Thinking About Your Page...",
    // type: "tool_call",
    component: () => (
      <NiceBadge
        caption="Deciding How to Approach Your Request..."
        icon={<BrainIcon className="w-4 h-4" />}
      />
    ),
  })),
  {
    id: "retrieve_item",
    type: "tool_call",
    component: () => (
      <NiceBadge
        caption="Retrieved an item from the page..."
        icon={<Target className="w-4 h-4" />}
      />
    ),
  },
  {
    id: "retrieve_item",
    hideMessage: true,
  },
  {
    id: "update_item",
    component: () => (
      <NiceBadge
        caption="Updated an item on the page..."
        icon={<PencilSimpleLine className="w-4 h-4" />}
      />
    ),
  },
  {
    id: "delete_item",
    component: () => (
      <NiceBadge
        caption="Deleted an item on the page..."
        icon={<Trash className="w-4 h-4" />}
      />
    ),
  },
  {
    id: "move_item",
    component: () => (
      <NiceBadge
        caption="Moved an item on the page..."
        icon={<HandSwipeRight className="w-4 h-4" />}
      />
    ),
  },
  {
    id: "write_item_copy",
    component: () => (
      <NiceBadge
        caption="Wrote copy for an item on the page..."
        icon={<PencilSimpleLine className="w-4 h-4" />}
      />
    ),
  },
];

const miscellaneousPageArtifacts: ArtifactType[] = [
  {
    id: "page-spec",
    title: "Page Spec",
    component: PageSpecFromStructuredMessage,
  },
  {
    id: "generate_page_spec",
    title: "Page Spec",
    component: PageSpecFromStructuredMessage,
  },
  {
    id: "section-content",
    title: `Building: Section Elements`,
    component: SectionBuilderFromStructuredMessage,
  },
  {
    id: "copy-associations",
    title: `Writing: Copy`,
    component: PageCopywritingFromStructuredMessage,
  },
  {
    id: "decide_theme_for_campaign",
    title: "Decide Theme for Campaign",
    component: () => (
      <NiceBadge
        caption={`Building: Design Theme...`}
        icon={<PaintBrush className="w-4 h-4" />}
      />
    ),
  },
  {
    id: "cot-message",
    component: ({ payload }: { payload: any }) => <div>{payload.message}</div>,
  },
  {
    id: "section-spec",
    component: () => null,
  },
  {
    id: "section-planner",
    component: ({ payload }: { payload: any }) => {
      const { sections = [], totalNumberOfSections = 0 } = payload;
      return (
      <div className="pt-4">
        <NiceBadge
          caption={`Planning your page`}
          icon={
            Number(sections?.length) > Number(totalNumberOfSections) ? (
              <CardsThree className="w-4 h-4 animate-bounce" />
            ) : (
              <CardsThree className="w-4 h-4" />
            )
          }
        />
        </div>
      );
    },
  },
  {
    id: "section-type",
    title: "Section Type",
    component: ({ payload }: { payload: any }) => (
      <MessageItem message={payload?.prompt} />
    ),
  },
];

export const pageBuilderArtifacts: ArtifactType[] = [
  ...sectionArtifacts,
  ...pageElementArtifacts,
  ...pageBuildingToolArtifacts,
  ...miscellaneousPageArtifacts,
];

export const campaignBuilderArtifacts: ArtifactType[] = [
  {
    id: "generate_campaign_spec",
    title: "Generate Campaign Spec",
    component: CampaignSpecFromStructuredMessage,
  },
  {
    id: "find_reference_funnel",
    title: "Find Reference Funnel",
    component: () => null,
  },
  {
    id: "create_nodes",
    title: "Create Nodes",
    component: () => null,
  },
  ...[
    "delete_nodes",
    "replace_nodes",
    "move_nodes",
    "update_node_details",
    "connect_nodes",
    "disconnect_nodes",
    "campaign_content",
  ].map((id) => ({
    id: id,
    title: id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    component: CampaignBuilderFromStructuredMessage,
  })),
  {
    id: "build_node_content",
    title: "Build Node Content",
    component: CampaignBuilderArtboardFromStructuredMessage,
  },
  {
    id: "build_node_content",
    title: "Build Node Content",
    type: "tool_call",
    component: BuildingNodeContent,
  }
];